import React, { useState } from 'react'
import { css, Global } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { ChangeReservationApprovedStatusModal } from '@/components/molecules/change-reservation-approved-status-modal'
import { ReservationType } from '@/models/reservation'
import { ApprovedStatusButton } from '@/components/molecules/approved-status-button'
import { RoomNumberPopup } from '@/components/molecules/room-number-popup'
import { Popover } from 'antd'
import { ReservationCopyModal } from '@/components/organisms/accommodation-management/reservation-copy-modal'
import { ReservationSubMenu } from './reservation-sub-menu'
import { updateReservationCheckinGuidance } from '@/apis/aipass'
import { useErrorHandler } from '@/hooks/use-error-handler'

type Props = {
  reservation: ReservationType
  onReload: () => void
  isAlertConfirmedSales: boolean
}

export const ReservationList: React.FC<Props> = props => {
  const { t } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const [openChangeApprovedStatusPopup, setOpenChangeApprovedStatusPopup] = useState(false)
  const [openListSubMenu, setOpenListSubMenu] = useState<boolean>(false)
  const [isReservationCopyModalOpen, setIsReservationCopyModalOpen] = useState<boolean>(false)
  const [reservationNoticeMessage, setReservationNoticeMessage] = useState<string>(props.reservation.checkinGuidanceMemo || '')

  const reservationSubMenu = () => (
    <ReservationSubMenu
      visible={openListSubMenu}
      onCopyReservation={() => {
        setOpenListSubMenu(false)
        setIsReservationCopyModalOpen(true)
      }}
      noticeMessage={{
        value: reservationNoticeMessage,
        onChange: setReservationNoticeMessage,
        onComplete: (memo: string) => {
          updateReservationCheckinGuidance({
            reservationId: props.reservation.reservationId,
            memo,
          }).catch(error => errorHandler(error))
        },
      }}
    />
  )

  return (
    <>
      <Global
        styles={css({
          '.ant-popover-inner-content': { padding: 'unset' },
          '.ant-popover-placement-bottomRight': { paddingTop: 0 },
          '.ant-popover-arrow': { display: 'none' },
        })}
      />
      <div css={reservationListContainer}>
        <div css={reservationListSubject}>
          <div css={{ color: '#272727' }}>{t('Reservation list')}</div>
        </div>
        <div style={{ overflowX: 'auto', borderRadius: '0px 0px 10px 10px' }}>
          <table style={{ width: '100%' }}>
            <thead>
              <tr css={listTitle}>
                <th css={reservationDateStyle}>{t('Date of stay')}</th>
                <th css={reservationUserStyle}>{t('Guest')}</th>
                <th css={roomNumberStyle}>{t('Room number')}</th>
                <th css={amountCancelledStyle}>{t('Amount to be collected')}</th>
                <th css={reservationStatusStyle}>{t('Status')}</th>
                <th css={updatedStyle}>{t('Update date')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              <tr css={listStyle}>
                <td css={reservationDateStyle}>
                  {`${
                    dayjs(props.reservation.checkinDate).format(t('MM-DD')) ? dayjs(props.reservation.checkinDate).format(t('MM-DD')) : ''
                  } ${props.reservation.nights ?? 0}${t('Night')}`}
                </td>
                <td css={reservationUserStyle}>{props.reservation.guestName || ''}</td>
                <td css={roomNumberStyle}>
                  <RoomNumberPopup rooms={props.reservation.assignedRoom || []} />
                </td>
                <td css={[amountCancelledStyle, { color: props.reservation.unpaid ? '#F2A40B' : '' }]}>
                  ¥{Number(props.reservation.unpaid || 0).toLocaleString()}
                </td>
                <td css={reservationStatusStyle}>
                  <ApprovedStatusButton
                    currentStatus={props.reservation.approvedStatus}
                    onClick={() => {
                      setOpenChangeApprovedStatusPopup(true)
                    }}
                  />
                  {openChangeApprovedStatusPopup && (
                    <ChangeReservationApprovedStatusModal
                      defaultStatus={props.reservation.approvedStatus}
                      defaultRoom={props.reservation.assignedRoom.map(r => r.guestRoomAssignId)}
                      reservationId={props.reservation.reservationId}
                      rooms={props.reservation.assignedRoom}
                      onClose={() => setOpenChangeApprovedStatusPopup(false)}
                      onChange={props.onReload}
                      isAlertConfirmedSales={props.isAlertConfirmedSales}
                      isDisabledSelect={true}
                    />
                  )}
                </td>
                <td css={updatedStyle}>
                  <p>
                    {t('Stay')}：{props.reservation.statusStayAt ? dayjs(props.reservation.statusStayAt).format(t('MM-DD HH:mm')) : '-'}
                  </p>
                  <p>
                    {t('Out')}：
                    {props.reservation.statusCheckoutAt ? dayjs(props.reservation.statusCheckoutAt).format(t('MM-DD HH:mm')) : '-'}
                  </p>
                </td>
                <td style={{ width: '5%' }}>
                  <Popover
                    placement="bottomRight"
                    trigger="click"
                    content={reservationSubMenu()}
                    visible={openListSubMenu}
                    onVisibleChange={visible => {
                      setOpenListSubMenu(visible)
                    }}
                  >
                    <div
                      css={css({
                        borderRadius: '50px',
                        width: '30px',
                        height: '30px',
                        cursor: 'pointer',
                        position: 'relative',
                        ':hover': { backgroundColor: '#f2f2f2' },
                      })}
                    >
                      <img src={require('@/static/images/icon_other.svg')} />
                    </div>
                  </Popover>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      {isReservationCopyModalOpen && (
        <ReservationCopyModal setIsModalOpen={setIsReservationCopyModalOpen} reservation={props.reservation} />
      )}
    </>
  )
}

const reservationListContainer = css({
  backgroundColor: '#fff',
  border: '1px solid #CCCCCC',
  borderRadius: '10px',
})

const listTitle = css({
  minWidth: 'max-content',
  color: '#676767',
  fontWeight: 'bold',
  padding: '10px 40px',
  fontSize: 12,
  height: 36,
  'th, td': {
    paddingBlock: 10,
    paddingLeft: 32,
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
    ':first-of-type': {
      '@media(max-width: 1080px)': {
        paddingLeft: 16,
      },
    },
    ':last-of-type': {
      paddingRight: 32,
      '@media(max-width: 1080px)': {
        paddingRight: 16,
      },
    },
  },
  th: {
    backgroundColor: '#f5f5f5',
  },
})

const listStyle = css(listTitle, {
  backgroundColor: '#fff',
  color: '#272727',
  fontWeight: 'normal',
  fontSize: 14,
  lineHeight: '24px',
  height: 56,
})

const reservationListSubject = css({
  fontSize: 14,
  fontWeight: 'bold',
  padding: '13px 34px 8px 32px',
  display: 'flex',
  justifyContent: 'space-between',
  '@media(max-width: 1080px)': {
    paddingInline: 16,
  },
})
const reservationDateStyle = css({ width: '12%' })
const reservationUserStyle = css({ width: '20%' })
const roomNumberStyle = css({
  width: '20%',
  '@media(max-width: 1080px)': {
    width: '9%',
    div: { maxWidth: 90 },
  },
})
const reservationStatusStyle = css({ width: '18%' })
const amountCancelledStyle = css({ width: '10%' })
const updatedStyle = css({
  width: '20%',
  p: { lineHeight: 1.2 },
})
